<template>
  <div>
    <b-modal
      modal-class="component-modal-product"
      id="stockAdjustmentModal"
      ref="stockAdjustmentModal"
      size="xl"
      :title="modalTitle"
      no-close-on-backdrop
      @hidden="handleHidden"
      @close="handleCloseEvent"
    >
      <b-form
        id="stockAdjustmentTable"
        @submit.prevent="saveStockAdjustment"
      >
        <b-form-group
          label-cols="3"
          label="รูปแบบการปรับสต๊อก:"
          label-align="right"
          class="align-label"
        >
          <b-form-radio-group v-model="stockType">
            <b-form-radio
              :value="STOCK_TYPE.WITHDRAW"
              :disabled="isStockTypeDisabled(STOCK_TYPE.WITHDRAW)"
              >ตัดเบิกของ</b-form-radio
            >
            <b-form-radio
              :value="STOCK_TYPE.RETURN"
              :disabled="isStockTypeDisabled(STOCK_TYPE.RETURN)"
              >ส่งคืนสินค้า</b-form-radio
            >
            <b-form-radio
              :value="STOCK_TYPE.GENERAL"
              :disabled="isStockTypeDisabled(STOCK_TYPE.GENERAL)"
              >ปรับปรุงทั่วไป</b-form-radio
            >
            <b-form-radio
              :value="STOCK_TYPE.PHYSICAL_COUNT"
              :disabled="isStockTypeDisabled(STOCK_TYPE.PHYSICAL_COUNT)"
              >ตรวจนับสต๊อก</b-form-radio
            >
          </b-form-radio-group>
          <small
            v-if="submitAttempted && $v.stockType.$error"
            class="text-danger d-block mt-1"
          >
            กรุณาเลือกรูปแบบการปรับสต๊อก
          </small>
        </b-form-group>
        <template v-if="stockType !== null && stockType !== ''">
          <b-form-group
            label-cols="3"
            label="วันที่มีผล:"
            label-align="right"
          >
            <DatePickerField
              v-model="transactionDate"
              :is-error="submitAttempted && $v.transactionDate.$error"
              :is-required="true"
              :show-error-icon="false"
              :disabled-date="isDateDisabled"
            />
            <small
              v-if="submitAttempted && !$v.transactionDate.required"
              class="text-danger d-block mt-1"
            >
              กรุณาระบุวันที่มีผล:
            </small>
            <small
              v-if="isDuplicatePhysicalCount && stockType === STOCK_TYPE.PHYSICAL_COUNT"
              class="text-danger d-block mt-1"
            >
              วันนี้มีการตรวจนับสต๊อกแล้ว กรุณาทำรายการใหม่ในวันถัดไป
            </small>
          </b-form-group>
          <b-form-group
            v-if="stockType === STOCK_TYPE.PHYSICAL_COUNT && !isDuplicatePhysicalCount"
            label-cols="3"
            label="วันที่นับจริง:"
            label-align="right"
          >
            <DatePickerField
              v-model="countedDt"
              :is-error="submitAttempted && $v.countedDt.$error"
              :is-required="true"
              :show-error-icon="false"
              :disabled-date="isCountedDateDisabled"
            />
            <small
              v-if="submitAttempted && !$v.countedDt.required"
              class="text-danger d-block mt-1"
            >
              กรุณาระบุวันที่นับจริง
            </small>
            <small
              v-if="submitAttempted && !$v.countedDt.minValue"
              class="text-danger d-block mt-1"
            >
              วันที่นับจริงต้องไม่น้อยกว่าวันที่มีผล
            </small>
          </b-form-group>
          <b-form-group
            v-if="!isDuplicatePhysicalCount"
            label-cols="3"
            label="เหตุผล:"
            label-align="right"
          >
            <textarea
              rows="1"
              class="form-control required no-scrollbar stock-reason-textarea"
              id="reasonStock"
              v-model="reasonStock"
              :class="{
                'is-invalid': $v.reasonStock.$error,
              }"
              :maxlength="reasonMaxLength()"
              placeholder="กรอกรายละเอียด"
              ref="reasonTextarea"
              @input="resizeTextarea"
            ></textarea>
            <template v-slot:description> </template>
          </b-form-group>
        </template>

        <template v-if="stockType === STOCK_TYPE.GENERAL">
          <GeneralAdjustmentStockTable
            :items="generalAdjustmentStock.productAdjustmentItems"
            :productList="getProductList"
            :productOptions="productOptions"
            :conversionUnitRate="conversionUnitRate"
            :validation="$v.generalAdjustmentStock.productAdjustmentItems.$each"
            :submitAttempted="submitAttempted"
            @update-conversion-rate="handleUpdateConversionRate"
            @add-item="addAdjustmentItem"
            @delete-item="
              payload => deleteAdjustmentItem(payload.item, payload.index)
            "
          />
        </template>

        <template v-if="stockType === STOCK_TYPE.RETURN">
          <b-form-group
            label-cols="3"
            label="รหัสใบรับสินค้าอ้างอิง:"
            label-align="right"
          >
            <div class="multiselect-wrapper">
              <multiselect
                class="col p-0 required"
                :class="{
                  'is-invalid': $v.returnProductAdjustmentStock.grNo.$error,
                }"
                v-model="returnProductAdjustmentStock.grNo"
                @input="onGRNoSelect(returnProductAdjustmentStock.grNo)"
                :options="GROptions"
                :multiple="false"
                placeholder="เลือกใบรับสินค้า (กรอกรหัสใบรับสินค้าเพื่อค้นหา)"
                track-by="id"
                :custom-label="customLabel"
                selectLabel="เลือกใบรับสินค้า"
                selectedLabel="เลือกแล้ว"
                deselectLabel="คลิกเพื่อลบออก"
                openDirection="below"
              >
                <span slot="noResult">ไม่พบรายการ แนะนำเปลี่ยนคำค้นหา</span>
              </multiselect>
            </div>
          </b-form-group>
          <template v-if="returnProductAdjustmentStock.grNo">
            <ReturnProductAdjustmentStockTable
              :items="normalProductAdjustmentItems"
            />
            <template v-if="bonusProductAdjustmentItems.length > 0">
              <div
                class="gr-bonus-header mt-4 mb-2 font-weight-bold"
                style="padding-left: 15px; font-size: 16px"
              >
                รายการสินค้าแถม
              </div>
              <ReturnProductAdjustmentStockTable
                :items="bonusProductAdjustmentItems"
              />
            </template>
          </template>
        </template>

        <template v-if="stockType === STOCK_TYPE.PHYSICAL_COUNT">
          <template v-if="!isDuplicatePhysicalCount">
            <b-form-group
              label-cols="3"
              label="ค้นหา:"
              label-align="right"
            >
              <b-form-input
                type="search"
                v-model="physicalFilter"
                placeholder="ค้นหาสินค้า จากรหัสหรือชื่อสินค้า"
              ></b-form-input>
            </b-form-group>
            <PhysicalProductAdjustmentStockTable
              :items="physicalProductAdjustmentStock.productAdjustmentItems"
              :filter="physicalFilter"
            />
          </template>
        </template>

        <template v-if="stockType === STOCK_TYPE.WITHDRAW">
          <WithdrawAdjustmentStockTable
            :items="withdrawAdjustmentStock.productAdjustmentItems"
            :productList="getProductList"
            :productOptions="productOptions"
            :conversionUnitRate="conversionUnitRate"
            :validation="
              $v.withdrawAdjustmentStock.productAdjustmentItems.$each
            "
            :submitAttempted="submitAttempted"
            @update-conversion-rate="handleUpdateConversionRate"
            @add-item="addWithdrawItem"
            @delete-item="
              payload => deleteWithdrawItem(payload.item, payload.index)
            "
          />
        </template>
      </b-form>

      <template v-slot:modal-footer>
        <div class="w-100 d-flex justify-content-end align-items-center">
          <b-button
            v-if="isEditMode"
            variant="danger"
            class="mr-2"
            @click="deleteStockAdjustment"
          >
            ยกเลิกรายการ
          </b-button>
          <b-button
            type="submit"
            form="stockAdjustmentTable"
            variant="primary"
          >
            {{ isEditMode ? "แก้ไขรายการ" : "ยืนยันสต๊อก" }}
          </b-button>
        </div>
      </template>
      <Dialog ref="Dialog"></Dialog>
      <Loading v-if="isLoading"></Loading>
    </b-modal>

    <PhysicalCountPreviewModal
      ref="physicalCountPreviewModal"
      :items="previewItems"
      :reason="reasonStock"
      :transactionDate="transactionDate"
      :countedDt="stockType === STOCK_TYPE.PHYSICAL_COUNT ? countedDt : null"
      :adjustmentNo="isEditMode ? editAdjustmentNo : ''"
      :createdByName="previewCreatedByName"
      :createdDate="isEditMode ? editCreatedDate : null"
      @confirm="confirmPreviewAndSubmit"
      @cancel="cancelPreview"
      @hidden="onPreviewModalHidden"
    />
  </div>
</template>

<script>
import moment from "moment";
import BigNumber from "bignumber.js";
import Dialog from "@/components/modal/Dialog";
import GeneralAdjustmentStockTable from "@/components/table/stock/GeneralAdjustmentStockTable.vue";
import ReturnProductAdjustmentStockTable from "@/components/table/stock/ReturnProductAdjustmentStockTable.vue";
import PhysicalProductAdjustmentStockTable from "@/components/table/stock/PhysicalProductAdjustmentStockTable.vue";
import WithdrawAdjustmentStockTable from "@/components/table/stock/WithdrawAdjustmentStockTable.vue";
import Loading from "@/components/Loading";
import DatePickerField from "@/components/table/stock/fields/DatePickerField.vue";
import PhysicalCountPreviewModal from "@/components/modal/ProductAndStock/PhysicalCountPreviewModal.vue";

import "@/theme/modal/ProductAndStock/Product.scss";
import { maxLength, required, requiredIf } from "vuelidate/lib/validators";
import { mapGetters, mapActions } from "vuex";
import {
  Status,
  STOCK_TYPE,
  STOCK_TYPE_API_NAME,
  STOCK_TYPE_FROM_API,
} from "@/constants/productStock";
import { ROLES } from "@/constants/user";
import {
  hasPhysicalCountInSelectedDate as checkPhysicalCountInSelectedDate,
  getLatestPhysicalCountDate,
} from "@/utils/stockValidation";

export default {
  name: "StockAdjustmentModal",
  components: {
    Dialog,
    Loading,
    GeneralAdjustmentStockTable,
    ReturnProductAdjustmentStockTable,
    PhysicalProductAdjustmentStockTable,
    WithdrawAdjustmentStockTable,
    DatePickerField,
    PhysicalCountPreviewModal,
  },
  data() {
    return {
      STOCK_TYPE,
      isLoading: false,
      isFromImport: false,
      isEditMode: false,
      editAdjustmentNo: "",
      editCreatedByName: "",
      editCreatedDate: null,
      submitAttempted: false,
      shouldRefreshOnClose: false,
      modalTitle: "ปรับปรุงสต๊อก",
      stockType: null,
      conversionUnitRate: [],
      reasonStock: "",
      physicalFilter: "",
      transactionDate: new Date(),
      countedDt: new Date(),
      // Preview confirmation modal state
      showPreviewModal: false,
      previewItems: [],
      pendingSubmitData: null,
      generalAdjustmentStock: {
        productAdjustmentItems: [
          {
            productNoAndName: "",
            adjustmentAmount: 0,
            productUnit: null,
            productAvailableStock: 0,
          },
        ],
      },
      returnProductAdjustmentStock: {
        grNo: "",
        productAdjustmentItems: [],
      },
      physicalProductAdjustmentStock: {
        productAdjustmentItems: [],
      },
      withdrawAdjustmentStock: {
        productAdjustmentItems: [
          {
            productNoAndName: "",
            adjustmentAmount: 0,
            productUnit: null,
            productAvailableStock: 0,
          },
        ],
      },
    };
  },
  validations: {
    stockType: {
      required(value) {
        return value !== "" && value !== null && value !== undefined;
      },
    },
    transactionDate: {
      required(value) {
        return value !== null && value !== undefined && value !== "";
      },
      notDuplicatePhysicalCount(value) {
        if (this.stockType !== STOCK_TYPE.PHYSICAL_COUNT || !value) return true;
        return !this.isDuplicatePhysicalCount;
      },
    },
    countedDt: {
      required: requiredIf(function () {
        return this.stockType === STOCK_TYPE.PHYSICAL_COUNT && !this.isDuplicatePhysicalCount;
      }),
      minValue(value) {
        if (this.stockType !== STOCK_TYPE.PHYSICAL_COUNT || this.isDuplicatePhysicalCount) return true;
        if (!value || !this.transactionDate) return true;
        const countedYMD = moment(value).startOf("day");
        const transactionYMD = moment(this.transactionDate).startOf("day");
        return countedYMD.isSameOrAfter(transactionYMD);
      },
    },
    reasonStock: {
      required,
      maxLength: maxLength(500),
    },
    generalAdjustmentStock: {
      productAdjustmentItems: {
        $each: {
          productNoAndName: {
            required(value) {
              return !!(value && value.id);
            },
          },
          adjustmentAmount: {
            required(value, row) {
              if (!row || !row.productNoAndName || !row.productNoAndName.id)
                return true;
              return value != null && Number(value) !== 0;
            },
          },
          currentStockToAfterAdjustment: {
            required(value, row) {
              if (!row || !row.productNoAndName || !row.productNoAndName.id)
                return true;

              const after = Number(value);
              const current = Number(row.productAvailableStock) || 0;
              return after !== current;
            },
          },
          productUnit: {
            required: requiredIf(function (row) {
              return !!(row && row.productNoAndName && row.productNoAndName.id);
            }),
          },
        },
      },
    },
    returnProductAdjustmentStock: {
      grNo: {
        required,
      },
    },
    physicalProductAdjustmentStock: {
      productAdjustmentItems: {
        $each: {
          adjustmentAmount: {
            required(value) {
              return value != null;
            },
          },
        },
      },
    },
    withdrawAdjustmentStock: {
      productAdjustmentItems: {
        $each: {
          productNoAndName: {
            required(value) {
              return !!(value && value.id);
            },
          },
          adjustmentAmount: {
            required(value, row) {
              if (!row || !row.productNoAndName || !row.productNoAndName.id)
                return true;
              return value != null && Number(value) > 0;
            },
          },
          currentStockToAfterAdjustment: {
            required(value, row) {
              if (!row || !row.productNoAndName || !row.productNoAndName.id)
                return true;

              const after = new BigNumber(value).minus(new BigNumber(row.adjustmentAmount).abs()).toNumber();
              const current = new BigNumber(row.productAvailableStock || 0).toNumber();
              return after !== current;
            },
          },
          productUnit: {
            required: requiredIf(function (row) {
              return !!(row && row.productNoAndName && row.productNoAndName.id);
            }),
          },
        },
      },
    },
  },
  computed: {
    productOptions() {
      return this.getProductList
        .filter(
          product =>
            product.productStatus === Status.LOCKED ||
            product.productStatus === Status.ACTIVE
        )
        .map(product => ({
          id: product.productId,
          name: `${product.productCode} - ${product.productNameMain}`,
          imageUrl: product.imageUrl || null,
        }));
    },
    GROptions() {
      return this.getAllGRWithProductList.map(item => ({
        id: item.grNo,
        name: item.grNo,
      }));
    },
    normalProductAdjustmentItems() {
      return this.returnProductAdjustmentStock.productAdjustmentItems.filter(
        item => item.productType !== "FREE"
      );
    },
    bonusProductAdjustmentItems() {
      return this.returnProductAdjustmentStock.productAdjustmentItems.filter(
        item => item.productType === "FREE"
      );
    },
    ...mapGetters({
      getProductList: "moduleProductInventory/getProductList",
      getAllGRWithProductList: "moduleProductInventory/getAllGRWithProductList",
      isAccessMenu: "moduleUser/getIsAccessMenu",
      getStockAdjustmentList: "moduleProductInventory/getStockAdjustmentList",
      userInfo: "moduleUser/getUserInfo",
    }),
    isSpecialStockAdjuster() {
      return this.isAccessMenu([ROLES.ROLE_SPECIAL_STOCK_ADJUSTER]);
    },
    isDuplicatePhysicalCount() {
      if (this.stockType !== STOCK_TYPE.PHYSICAL_COUNT || !this.transactionDate) return false;
      return checkPhysicalCountInSelectedDate(
        this.getStockAdjustmentList,
        this.transactionDate,
        this.isEditMode ? this.editAdjustmentId : null
      );
    },
    latestPhysicalCountDate() {
      return getLatestPhysicalCountDate(
        this.getStockAdjustmentList,
        this.isEditMode ? this.editAdjustmentId : null
      );
    },
    previewCreatedByName() {
      if (this.isEditMode) {
        return this.editCreatedByName || "-";
      }
      if (this.userInfo) {
        return (
          [this.userInfo.firstNameTh, this.userInfo.lastNameTh]
            .filter(Boolean)
            .join(" ") || "-"
        );
      }
      return "-";
    },
  },
  methods: {
    ...mapActions({
      fetchAllGRWithProductList:
        "moduleProductInventory/fetchAllGRWithProductList",
    }),
    isStockTypeDisabled(type) {
      if (type !== STOCK_TYPE.PHYSICAL_COUNT && this.isFromImport) return true;
      return this.isEditMode && this.stockType !== type;
    },
    reasonMaxLength() {
      if (this.stockType === STOCK_TYPE.RETURN) return 79;
      return 100;
    },
    resizeTextarea() {
      const el = this.$refs.reasonTextarea;
      if (!el) return;
      el.style.height = "40px"; // Reset height to recalculate
      let scrollHeight = el.scrollHeight;
      if (scrollHeight < 40) scrollHeight = 40; // Protect against modal transition 0-height
      // Max height limit around 7 rows (~180px)
      if (scrollHeight > 180) {
        el.style.height = "180px";
        el.style.overflowY = "auto";
      } else {
        el.style.height = scrollHeight + "px";
        el.style.overflowY = "hidden";
      }
    },
    async show(data) {
      const { physicalAdjustmentItems, editData } = data;

      this.isLoading = true;
      await this.reloadProductList();
      this.isLoading = false;

      if (editData) {
        // Edit mode: populate form with existing data
        this.isEditMode = true;
        this.editAdjustmentId = editData.id;
        this.editAdjustmentNo = editData.adjustmentNo || "";
        this.editCreatedByName =
          [editData.createdBy?.firstNameTh, editData.createdBy?.lastNameTh]
            .filter(Boolean)
            .join(" ") || "-";
        this.editCreatedDate = editData.createdDate || null;
        this.reasonStock = editData.reason || "";
        this.transactionDate = editData.transactionDate
          ? new Date(editData.transactionDate)
          : new Date();
        this.countedDt = editData.countedDt
          ? new Date(editData.countedDt)
          : new Date();

        this.stockType = STOCK_TYPE_FROM_API[editData.type] ?? null;

        this.$nextTick(() => {
          this.populateEditData(editData);
        });
      } else if (
        physicalAdjustmentItems &&
        physicalAdjustmentItems.length > 0
      ) {
        this.isEditMode = false;
        this.editAdjustmentId = null;
        this.editAdjustmentNo = "";
        this.editCreatedByName = "";
        this.editCreatedDate = null;
        this.stockType = STOCK_TYPE.PHYSICAL_COUNT;
        this.isFromImport = true;
        this.$nextTick(() => {
          this.physicalProductAdjustmentStock.productAdjustmentItems =
            physicalAdjustmentItems;
        });
      } else {
        this.isEditMode = false;
        this.editAdjustmentId = null;
        this.editAdjustmentNo = "";
        this.editCreatedByName = "";
        this.editCreatedDate = null;
        this.isFromImport = false;
        this.getPhysicalProductAdjustmentStock();
      }

      this.$nextTick(() => {
        this.resizeTextarea();
      });

      this.$bvModal.show("stockAdjustmentModal");
    },
    handleHidden() {
      this.$v.$reset();
      this.setDefaultValue();

      if (this.shouldRefreshOnClose) {
        this.$emit("stock-adjusted");
        this.shouldRefreshOnClose = false;
      }
    },
    handleCloseEvent() {
      this.close();
    },
    close() {
      this.$bvModal.hide("stockAdjustmentModal");
    },
    reloadProductList() {
      this.$store.dispatch("moduleProductInventory/fetchProductList", {
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      });
    },
    reloadGRList() {
      this.fetchAllGRWithProductList({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      });
    },
    setDefaultValue() {
      this.submitAttempted = false;
      this.isFromImport = false;
      this.isEditMode = false;
      this.editAdjustmentId = null;
      this.editAdjustmentNo = "";
      this.editCreatedByName = "";
      this.editCreatedDate = null;
      this.stockType = "";
      this.reasonStock = "";
      this.transactionDate = new Date();
      this.countedDt = new Date();
      this.physicalFilter = "";
      this.conversionUnitRate = [];
      this.generalAdjustmentStock = {
        productAdjustmentItems: [
          {
            productNoAndName: "",
            adjustmentAmount: 0,
            productUnit: null,
            currentStockToAfterAdjustment: null,
          },
        ],
      };
      this.returnProductAdjustmentStock = {
        grNo: "",
        productAdjustmentItems: [],
      };
      this.physicalProductAdjustmentStock = {
        productAdjustmentItems: [],
      };
      this.withdrawAdjustmentStock = {
        productAdjustmentItems: [
          {
            productNoAndName: "",
            adjustmentAmount: 0,
            productUnit: null,
            currentStockToAfterAdjustment: null,
            productAvailableStock: 0,
          },
        ],
      };
    },
    getAdjustmentProductsForSubmit() {
      const type = this.stockType;
      if (type === STOCK_TYPE.GENERAL) {
        return this.generalAdjustmentStock.productAdjustmentItems.map(item => ({
          productId: item.productNoAndName.id,
          qtyUom: item.adjustmentAmount,
          uomId: item.productUnit.id,
        }));
      }
      if (type === STOCK_TYPE.WITHDRAW) {
        return this.withdrawAdjustmentStock.productAdjustmentItems.map(
          item => ({
            productId: item.productNoAndName.id,
            // User enters a positive number; negate it so the API receives a negative qty (stock decrease)
            qtyUom: new BigNumber(item.adjustmentAmount).abs().negated().toNumber(),
            uomId: item.productUnit.id,
          })
        );
      }
      if (type === STOCK_TYPE.RETURN) {
        return this.returnProductAdjustmentStock.productAdjustmentItems
          .filter(item => Number(item.adjustmentAmount) > 0)
          .map(item => ({
            productId: item.productId,
            // User enters a positive number; negate it so the API receives a negative qty (stock decrease)
            qtyUom: new BigNumber(item.adjustmentAmount).abs().negated().toNumber(),
            uomId: item.productUnitId,
            productType: item.productType,
          }));
      }
      if (type === STOCK_TYPE.PHYSICAL_COUNT) {
        return this.physicalProductAdjustmentStock.productAdjustmentItems.map(
          item => ({
            productId: item.productId,
            countedQtyUom:
              item.currentStockToAfterAdjustment !== null &&
              item.currentStockToAfterAdjustment !== undefined &&
              item.currentStockToAfterAdjustment !== ""
                ? Number(item.currentStockToAfterAdjustment)
                : null,
            uomId: item.productUnitId,
          })
        );
      }
      return [];
    },
    deleteStockAdjustment() {
      this.$refs.Dialog.showAlertConfirm(
        "ยกเลิกการจัดการสต๊อก ?",
        "คุณต้องการยกเลิกการจัดการสต๊อกนี้ใช่หรือไม่"
      ).then(result => {
        if (!result.value) return;
        this.isLoading = true;

        this.$store
          .dispatch("moduleProductInventory/deleteStockAdjustment", {
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
            adjustmentId: this.editAdjustmentId,
          })
          .then(res => {
            if (res.status === 200) {
              this.reloadProductList();
              this.reloadGRList();
              this.shouldRefreshOnClose = true;
              this.close();
              this.$parent.showDialogToast("success", "ยกเลิกการจัดการสต๊อกสำเร็จ");
            }
          })
          .catch(err => {
            const errorMessage = err.response?.data?.errorMessage;
            let errorMsg =
              typeof errorMessage === "string"
                ? errorMessage
                : "ยกเลิการจัดการสต๊อกไม่สำเร็จ กรุณาลองใหม่";

            if (
              errorMsg === "ไม่พบข้อมูลการปรับปรุงสต๊อก" ||
              err.response?.status === 404
            ) {
              errorMsg =
                "รายการนี้ถูกลบไปแล้ว กรุณารีเฟรชหน้าเพื่ออัปเดตข้อมูล";
            }

            this.$parent.showDialogToast("danger", errorMsg);

            if (
              errorMsg ===
              "รายการนี้ถูกลบไปแล้ว กรุณารีเฟรชหน้าเพื่ออัปเดตข้อมูล"
            ) {
              this.shouldRefreshOnClose = true;
              this.close();
            }
          })
          .finally(() => {
            this.isLoading = false;
          });
      });
    },
    async saveStockAdjustment() {
      this.submitAttempted = true;
      this.$v.$touch();

      const stockTypeInvalid = this.$v.stockType.$invalid;
      const reasonStockInvalid = this.$v.reasonStock.$invalid;
      const transactionDateInvalid = this.$v.transactionDate.$invalid;
      const countedDtInvalid = this.$v.countedDt.$invalid;
      const activeFormInvalid =
        this.stockType === STOCK_TYPE.GENERAL
          ? this.$v.generalAdjustmentStock.$invalid
          : this.stockType === STOCK_TYPE.RETURN
          ? this.$v.returnProductAdjustmentStock.$invalid
          : this.stockType === STOCK_TYPE.WITHDRAW
          ? this.$v.withdrawAdjustmentStock.$invalid
          : this.$v.physicalProductAdjustmentStock.$invalid;

      if (
        stockTypeInvalid ||
        reasonStockInvalid ||
        transactionDateInvalid ||
        countedDtInvalid ||
        activeFormInvalid
      ) {
        return;
      }

      this.isLoading = true;

      const adjustStockData = {
        type: STOCK_TYPE_API_NAME[this.stockType],
        reason: this.reasonStock,
        transactionDate: (() => {
          const selectedDate = moment(this.transactionDate).format(
            "YYYY-MM-DD"
          );
          const currentTimeUtc = moment().utc().format("HH:mm:ss.SSS[Z]");
          return `${selectedDate}T${currentTimeUtc}`;
        })(),
        ...(this.stockType === STOCK_TYPE.RETURN && {
          refDocNo:
            this.returnProductAdjustmentStock.grNo?.name ||
            this.returnProductAdjustmentStock.grNo,
        }),
        ...(this.stockType === STOCK_TYPE.PHYSICAL_COUNT && {
          countedDt: (() => {
            const selectedDate = moment(this.countedDt).format("YYYY-MM-DD");
            const currentTimeUtc = moment().utc().format("HH:mm:ss.SSS[Z]");
            return `${selectedDate}T${currentTimeUtc}`;
          })(),
        }),
        adjustmentProducts: this.getAdjustmentProductsForSubmit(),
      };

      // For PHYSICAL_COUNT: show preview confirmation modal first
      if (this.stockType === STOCK_TYPE.PHYSICAL_COUNT) {
        this.pendingSubmitData = adjustStockData;
        await this.fetchAndShowPreview(adjustStockData);
        return;
      }

      // For other types: submit directly
      this.executeSubmit(adjustStockData);
    },
    async fetchAndShowPreview(adjustStockData) {
      try {
        const previewPayload = {
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          adjustStockData: {
            ...adjustStockData,
            ...(this.isEditMode && { adjustmentId: this.editAdjustmentId }),
          },
        };

        const res = await this.$store.dispatch(
          "moduleProductInventory/previewPhysicalCountAdjustment",
          previewPayload
        );

        if (res.status === 200 && res.data?.data) {
          // Enrich preview items with UOM name from current product items
          const physicalItems =
            this.physicalProductAdjustmentStock.productAdjustmentItems;
          this.previewItems = res.data.data.map(item => {
            const physicalItem = physicalItems.find(
              p => p.productId === item.productId
            );
            return {
              ...item,
              uomName: physicalItem?.productUnit || "-",
            };
          });
          this.isLoading = false;
          this.$refs.physicalCountPreviewModal.show();
        }
      } catch (err) {
        this.isLoading = false;
        const errorMessage = err.response?.data?.errorMessage;
        const errorMsg =
          typeof errorMessage === "string"
            ? errorMessage
            : "ไม่สามารถดึงข้อมูลตัวอย่างได้ กรุณาลองใหม่";
        this.$parent.showDialogToast("danger", errorMsg);
      }
    },
    cancelPreview() {
      this.previewItems = [];
      this.pendingSubmitData = null;
    },
    onPreviewModalHidden() {
      // Clean up if modal dismissed by ESC or backdrop
      if (this.pendingSubmitData) {
        this.previewItems = [];
        this.pendingSubmitData = null;
      }
    },
    confirmPreviewAndSubmit() {
      if (this.pendingSubmitData) {
        this.isLoading = true;
        this.executeSubmit(this.pendingSubmitData);
        this.pendingSubmitData = null;
        this.previewItems = [];
      }
    },
    executeSubmit(adjustStockData) {
      const dispatchAction = this.isEditMode
        ? "moduleProductInventory/updateStockAdjustment"
        : "moduleProductInventory/createStockAdjustment";

      const dispatchPayload = {
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        adjustStockData: adjustStockData,
        ...(this.isEditMode && { adjustmentId: this.editAdjustmentId }),
      };

      const successStatus = this.isEditMode ? 200 : 201;
      const successMsg = this.isEditMode
        ? "แก้ไขการจัดการสต๊อกสำเร็จ"
        : "ปรับปรุงสต๊อกสำเร็จ";
      const errorDefaultMsg = this.isEditMode
        ? "แก้ไขการจัดการสต๊อกไม่สำเร็จ กรุณาลองใหม่"
        : "ปรับปรุงสต๊อกไม่สำเร็จ กรุณาลองใหม่";

      this.$store
        .dispatch(dispatchAction, dispatchPayload)
        .then(res => {
          if (res.status === successStatus) {
            this.reloadProductList();
            this.reloadGRList();
            this.shouldRefreshOnClose = true;
            this.close();
            this.$parent.showDialogToast("success", successMsg);
          }
        })
        .catch(err => {
          const errorMessage = err.response?.data?.errorMessage;

          let errorMsg =
            typeof errorMessage === "string" ? errorMessage : errorDefaultMsg;

          if (
            errorMsg === "ไม่พบข้อมูลการปรับปรุงสต๊อก" ||
            err.response?.status === 404
          ) {
            errorMsg = "รายการนี้ถูกลบไปแล้ว กรุณารีเฟรชหน้าเพื่ออัปเดตข้อมูล";
          }

          this.$parent.showDialogToast("danger", errorMsg);

          if (
            errorMsg === "รายการนี้ถูกลบไปแล้ว กรุณารีเฟรชหน้าเพื่ออัปเดตข้อมูล"
          ) {
            this.shouldRefreshOnClose = true;
            this.close();
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    customLabel(item) {
      return item?.name || "";
    },
    createEmptyAdjustmentItem() {
      return {
        productNoAndName: "",
        adjustmentAmount: 0,
        productUnit: null,
        currentStockToAfterAdjustment: 0,
        productAvailableStock: 0,
      };
    },
    addAdjustmentItem() {
      this.generalAdjustmentStock.productAdjustmentItems.push(
        this.createEmptyAdjustmentItem()
      );
    },
    deleteAdjustmentItem(_item, index) {
      this.generalAdjustmentStock.productAdjustmentItems.splice(index, 1);
    },
    addWithdrawItem() {
      this.withdrawAdjustmentStock.productAdjustmentItems.push(
        this.createEmptyAdjustmentItem()
      );
    },
    deleteWithdrawItem(_item, index) {
      this.withdrawAdjustmentStock.productAdjustmentItems.splice(index, 1);
    },
    handleUpdateConversionRate(newRates) {
      const newIds = new Set(newRates.map(r => r.id));
      this.conversionUnitRate = this.conversionUnitRate.filter(
        r => !newIds.has(r.id)
      );
      newRates.forEach(r => this.conversionUnitRate.push(r));
    },
    resetFormOnStockTypeChange() {
      this.$v.$reset();
      this.submitAttempted = false;
      this.reasonStock = "";
      this.transactionDate = new Date();
      this.physicalFilter = "";
      this.conversionUnitRate = [];
      this.generalAdjustmentStock = {
        productAdjustmentItems: [this.createEmptyAdjustmentItem()],
      };
      this.returnProductAdjustmentStock = {
        grNo: "",
        productAdjustmentItems: [],
      };
      this.physicalProductAdjustmentStock = {
        productAdjustmentItems: [],
      };
      this.withdrawAdjustmentStock = {
        productAdjustmentItems: [this.createEmptyAdjustmentItem()],
      };
      this.getPhysicalProductAdjustmentStock();
    },
    onGRNoSelect(selected) {
      if (!selected) return;
      const grNo = selected.name;
      if (grNo) this.mapGRToProductListData(grNo);
    },
    mapGRToProductListData(grNo) {
      const gr = this.getAllGRWithProductList.find(item => item.grNo === grNo);

      if (!gr) return;

      const productListData = gr.grProduct.map(item => {
        const productData = this.getProductList.find(
          p => p.productId === item.product.productId
        );
        const maxReturn = Number(item.remainingReturnableQty) || 0;
        return {
          productId: item.product.productId,
          productNoAndName: `${item.product.productCode} - ${item.product.productNameMain}`,
          imageUrl: productData?.imageUrl || null,
          productAvailableStock: item.stockAvailable,
          productUnit: item.unit.unitNameTh,
          productUnitId: item.unit.unitId,
          productQtyOnGR: item.stockOnGR,
          productType: item.productType || "NORMAL",
          adjustmentAmount: 0,
          currentStockToAfterAdjustment: item.stockAvailable,
          remainingReturnableQty: item.remainingReturnableQty,
          maxReturnAmount: maxReturn,
        };
      });

      this.returnProductAdjustmentStock.productAdjustmentItems =
        productListData;
    },
    getPhysicalProductAdjustmentStock() {
      const productListData = this.getProductList
        .filter(
          item =>
            item.productStatus === Status.LOCKED ||
            item.productStatus === Status.ACTIVE
        )
        .map(item => ({
          productId: item.productId,
          productCode: item.productCode,
          productNameMain: item.productNameMain,
          productNoAndName: `${item.productCode} - ${item.productNameMain}`,
          imageUrl: item.imageUrl || null,
          productAvailableStock: item.productAvailableStock,
          productUnit: item.productUnit?.description ?? "",
          productUnitId: item.productUnit?.id ?? null,
          adjustmentAmount: 0,
          currentStockToAfterAdjustment: item.productAvailableStock,
        }));
      this.physicalProductAdjustmentStock.productAdjustmentItems =
        productListData;
    },
    populateEditData(editData) {
      const type = editData.type;
      const ledgers = editData.stockLedgers || [];

      if (type === "GENERAL") {
        // GENERAL: signed adjustmentAmount — negative means decrease, positive means increase
        const items = ledgers.map(ledger => {
          const product = this.getProductList.find(
            p => p.productId === ledger.productId
          );
          const currentStock = product?.productAvailableStock || 0;
          const preAdjustmentStock = currentStock;
          return {
            productNoAndName: product
              ? {
                  id: product.productId,
                  name: `${product.productCode} - ${product.productNameMain}`,
                  imageUrl: product.imageUrl || null,
                }
              : {
                  id: ledger.productId,
                  name: ledger.product
                    ? `${ledger.product.no} - ${ledger.product.description}`
                    : `Product #${ledger.productId}`,
                  imageUrl: null,
                },
            adjustmentAmount: Number(ledger.qtyUom),
            productUnit: ledger.uom
              ? {
                  id: ledger.uom.id,
                  name: ledger.uom.description,
                  code: ledger.uom.code,
                  description: ledger.uom.description,
                }
              : null,
            productAvailableStock: preAdjustmentStock,
            currentStockToAfterAdjustment: currentStock,
          };
        });
        this.generalAdjustmentStock.productAdjustmentItems = items;
      } else if (type === "WITHDRAW") {
        // WITHDRAW: user-facing value is always positive; ledger stores negative
        const items = ledgers.map(ledger => {
          const product = this.getProductList.find(
            p => p.productId === ledger.productId
          );
          const currentStock = product?.productAvailableStock || 0;
          // ledger.qtyUom is negative for withdraw; reverse it to get pre-adjustment stock
          const preAdjustmentStock = currentStock;
          return {
            productNoAndName: product
              ? {
                  id: product.productId,
                  name: `${product.productCode} - ${product.productNameMain}`,
                  imageUrl: product.imageUrl || null,
                }
              : {
                  id: ledger.productId,
                  name: ledger.product
                    ? `${ledger.product.no} - ${ledger.product.description}`
                    : `Product #${ledger.productId}`,
                  imageUrl: null,
                },
            // Display as positive (user-facing); ledger stores negative
            adjustmentAmount: new BigNumber(ledger.qtyUom).abs().toNumber(),
            productUnit: ledger.uom
              ? {
                  id: ledger.uom.id,
                  name: ledger.uom.description,
                  code: ledger.uom.code,
                  description: ledger.uom.description,
                }
              : null,
            productAvailableStock: preAdjustmentStock,
            currentStockToAfterAdjustment: currentStock,
          };
        });
        this.withdrawAdjustmentStock.productAdjustmentItems = items;
      } else if (type === "RETURN") {
        // For RETURN type, set the GR reference and populate items
        if (editData.refDocNo) {
          this.returnProductAdjustmentStock.grNo = {
            id: editData.refDocNo,
            name: editData.refDocNo,
          };
          this.mapGRToProductListData(editData.refDocNo);

          const items =
            this.returnProductAdjustmentStock.productAdjustmentItems.map(
              item => {
                const ledger = ledgers.find(
                  l =>
                    l.productId === item.productId &&
                    (l.productType || "NORMAL") ===
                      (item.productType || "NORMAL")
                );

                if (ledger) {
                  const currentStock = item.productAvailableStock;
                  const absQty = new BigNumber(ledger.qtyUom).abs().toNumber();
                  const preAdjustmentStock = new BigNumber(currentStock).plus(absQty).toNumber();

                  // Display as positive (user-facing)
                  item.adjustmentAmount = absQty;
                  item.productAvailableStock = preAdjustmentStock;
                  item.currentStockToAfterAdjustment = currentStock;

                  // Since we are editing this exact adjustment, its quantity shouldn't be counted against the remaining limit yet
                  item.remainingReturnableQty =
                    Number(item.remainingReturnableQty || 0) + absQty;
                  item.maxReturnAmount = item.remainingReturnableQty;
                }
                return item;
              }
            );
          this.returnProductAdjustmentStock.productAdjustmentItems = items;
        } else {
          const items = ledgers.map(ledger => {
            const product = this.getProductList.find(
              p => p.productId === ledger.productId
            );
            const currentStock = product?.productAvailableStock || 0;

            return {
              productId: ledger.productId,
              productNoAndName: product
                ? `${product.productCode} - ${product.productNameMain}`
                : ledger.product
                ? `${ledger.product.no} - ${ledger.product.description}`
                : `Product #${ledger.productId}`,
              imageUrl: product?.imageUrl || null,
              productAvailableStock:
                new BigNumber(currentStock).plus(new BigNumber(ledger.qtyUom).abs()).toNumber(),
              productUnit: ledger.uom?.description || "",
              productUnitId: ledger.uomId,
              productType: ledger.productType || "NORMAL",
              // Display as positive (user-facing); ledger stores negative
              adjustmentAmount: new BigNumber(ledger.qtyUom).abs().toNumber(),
              currentStockToAfterAdjustment: currentStock,
            };
          });

          this.returnProductAdjustmentStock.productAdjustmentItems = items;
        }
      } else if (type === "PHYSICAL_COUNT") {
        // For PHYSICAL_COUNT, use the full product list and apply existing ledger values
        const ledgerMap = new Map(ledgers.map(l => [l.productId, l]));

        const productListData = this.getProductList
          .filter(
            item =>
              item.productStatus === Status.LOCKED ||
              item.productStatus === Status.ACTIVE
          )
          .map(item => {
            const ledger = ledgerMap.get(item.productId);
            const adjustmentAmount = ledger ? new BigNumber(ledger.qtyUom).toNumber() : 0;
            // Subtract existing adjustment to get pre-adjustment baseline
            const preAdjustmentStock = new BigNumber(item.productAvailableStock).minus(adjustmentAmount).toNumber();

            let afterAdjustment = item.productAvailableStock;
            if (
              ledger &&
              ledger.countedQtyUom !== null &&
              ledger.countedQtyUom !== undefined
            ) {
              afterAdjustment = Number(ledger.countedQtyUom);
            }

            return {
              productId: item.productId,
              productCode: item.productCode,
              productNameMain: item.productNameMain,
              productNoAndName: `${item.productCode} - ${item.productNameMain}`,
              imageUrl: item.imageUrl || null,
              productAvailableStock: preAdjustmentStock,
              productUnit: item.productUnit?.description ?? "",
              productUnitId: item.productUnit?.id ?? null,
              adjustmentAmount: adjustmentAmount,
              currentStockToAfterAdjustment: afterAdjustment,
            };
          });

        this.physicalProductAdjustmentStock.productAdjustmentItems =
          productListData;
      }
    },
    isDateDisabled(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const target = new Date(date);
      target.setHours(0, 0, 0, 0);

      // No future dates for anyone
      if (target > today) return true;

      // Restriction for Physical Count: must be after the latest existing count
      if (this.stockType === STOCK_TYPE.PHYSICAL_COUNT && this.latestPhysicalCountDate) {
        const latestDate = new Date(this.latestPhysicalCountDate);
        latestDate.setHours(0, 0, 0, 0);
        if (target <= latestDate) return true;
      }

      // Special role: allow all past dates (that passed the physical count check)
      if (this.isSpecialStockAdjuster) return false;

      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();
      const targetMonth = target.getMonth();
      const targetYear = target.getFullYear();
      const monthsDiff =
        (currentYear - targetYear) * 12 + (currentMonth - targetMonth);

      // Current month: always selectable
      if (monthsDiff === 0) return false;

      // Previous month: selectable only if today is <= 3rd
      if (monthsDiff === 1 && today.getDate() <= 3) return false;

      return true;
    },
    isCountedDateDisabled(date) {
      if (!date) return false;

      const target = moment(date).startOf("day");
      const today = moment().startOf("day");

      // No future dates
      if (target.isAfter(today)) return true;

      // Must be same or after transactionDate
      if (this.transactionDate) {
        const transDate = moment(this.transactionDate).startOf("day");
        if (target.isBefore(transDate)) return true;
      }

      return false;
    },
  },
  watch: {
    stockType(newVal, oldVal) {
      // Skip reset when first setting type in edit mode (oldVal is "" or null)
      if (this.isEditMode && (oldVal === "" || oldVal === null)) {
        return;
      }
      this.resetFormOnStockTypeChange();
    },
    reasonStock() {
      this.$nextTick(() => {
        this.resizeTextarea();
      });
    },
    transactionDate(newVal) {
      if (newVal && this.stockType === STOCK_TYPE.PHYSICAL_COUNT) {
        const transMoment = moment(newVal).startOf("day");
        const countedMoment = moment(this.countedDt).startOf("day");
        if (countedMoment.isBefore(transMoment)) {
          this.countedDt = new Date(newVal);
        }
      }
    },
  },
};
</script>

<style scoped>
::v-deep .stock-reason-textarea {
  min-height: 40px;
}

::v-deep .stock-reason-textarea.is-invalid,
::v-deep .was-validated .stock-reason-textarea:invalid {
  background-image: none !important;
  padding-right: 0.75rem !important;
}

::v-deep .align-label .col-form-label {
  padding-top: 0px !important;
}

::v-deep .multiselect__tags {
  background-color: #ccecff6c;
  box-shadow: none;
  /* border-left: none !important; */
  overflow: hidden;
}

::v-deep .multiselect__single,
::v-deep .multiselect__input,
::v-deep .multiselect__placeholder {
  background-color: transparent !important;
  /* border-left: none !important; */
  overflow: hidden;
}

::v-deep span.multiselect__placeholder {
  margin-left: 6px !important;
}

::v-deep .multiselect__input,
::v-deep .multiselect__single {
  padding-left: 6px;
  /* border-left: none !important; */
  overflow: hidden;
}

::v-deep span.multiselect__single {
  margin-left: 6px !important;
  /* border-left: none !important; */
  overflow: hidden;
}

::v-deep .multiselect__single {
  margin-bottom: 0;
  padding-left: 0;
  /* border-left: none !important; */
  overflow: hidden;
}

::v-deep .multiselect__tags {
  box-shadow: none;
}

::v-deep .multiselect--disabled {
  background-color: #ebebeb !important;
  color: #c6c6c6 !important;
  opacity: 1 !important;
}

::v-deep .multiselect--disabled .multiselect__tags {
  background-color: #ebebeb !important;
  cursor: not-allowed;
  overflow: hidden;
  color: #c6c6c6 !important;
}

::v-deep .multiselect--disabled .multiselect__placeholder {
  background-color: #ebebeb !important;
  color: #c6c6c6 !important;
}

::v-deep .multiselect--disabled .multiselect__select {
  background-color: #ebebeb !important;
  cursor: not-allowed;
  border: 1px solid #ced4da !important;
  border-left: none !important;
  overflow: hidden;

  border-radius: 4px;
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}

::v-deep .multiselect__element .multiselect__option {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

::v-deep #GeneralAdjustmentStockTable tbody tr td {
  border-top: none !important;
  border-bottom: 1px solid #dee2e6 !important;
}

::v-deep #stockAdjustmentModal .modal-body {
  padding-bottom: 0 !important;
}

::v-deep #GeneralAdjustmentStockTable tbody tr:last-child td {
  border-bottom: none !important;
}

::v-deep #ReturnProductAdjustmentStockTable tbody tr td {
  border-top: none !important;
  border-bottom: 1px solid #dee2e6 !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

::v-deep #ReturnProductAdjustmentStockTable tbody tr:last-child td {
  border-bottom: none !important;
}

::v-deep .bottom-add-row {
  text-align: left !important;
  margin-top: 5px;
}

::v-deep #GeneralAdjustmentStockTable.table-hover tbody tr:last-child:hover {
  background-color: transparent !important;
}

::v-deep #GeneralAdjustmentStockTable thead tr th:nth-child(1),
::v-deep
  #GeneralAdjustmentStockTable
  tbody
  tr:not(:last-child)
  td:nth-child(1) {
  text-align: center !important;
  vertical-align: middle !important;
}

::v-deep #ReturnProductAdjustmentStockTable thead tr th,
::v-deep #ReturnProductAdjustmentStockTable tbody tr td {
  text-align: left !important;
  vertical-align: middle !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

::v-deep #GeneralAdjustmentStockTable .bottom-row-cell {
  text-align: left !important;
}

::v-deep .multiselect-unit .multiselect__tags {
  overflow: visible;
  min-width: 0;
}

::v-deep .multiselect-unit .multiselect__single,
::v-deep .multiselect-unit span.multiselect__single {
  white-space: normal !important;
  word-break: break-word;
  overflow-wrap: break-word;
  overflow: visible !important;
}

::v-deep #GeneralAdjustmentStockTable thead tr th,
::v-deep #GeneralAdjustmentStockTable tbody tr td {
  vertical-align: middle !important;
}

::v-deep #ReturnProductAdjustmentStockTable td.text-wrap-cell {
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

::v-deep #PhysicalProductAdjustmentStockTable thead tr th,
::v-deep #PhysicalProductAdjustmentStockTable tbody tr td {
  vertical-align: middle !important;
}

::v-deep #PhysicalProductAdjustmentStockTable td.text-wrap-cell {
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

::v-deep #WithdrawAdjustmentStockTable tbody tr td {
  border-top: none !important;
  border-bottom: 1px solid #dee2e6 !important;
}

::v-deep #WithdrawAdjustmentStockTable tbody tr:last-child td {
  border-bottom: none !important;
}

::v-deep #WithdrawAdjustmentStockTable.table-hover tbody tr:last-child:hover {
  background-color: transparent !important;
}

::v-deep #WithdrawAdjustmentStockTable thead tr th:nth-child(1),
::v-deep
  #WithdrawAdjustmentStockTable
  tbody
  tr:not(:last-child)
  td:nth-child(1) {
  text-align: center !important;
  vertical-align: middle !important;
}

::v-deep #WithdrawAdjustmentStockTable .bottom-row-cell {
  text-align: left !important;
}

::v-deep #WithdrawAdjustmentStockTable thead tr th,
::v-deep #WithdrawAdjustmentStockTable tbody tr td {
  vertical-align: middle !important;
}

::v-deep .delete-btn {
  background-color: transparent;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  color: #a3a4a6;
  font-size: 20px;
  width: 48px;
  height: 38px;
}

::v-deep .custom-radio.disabled,
::v-deep .custom-radio input[type="radio"]:disabled ~ label {
  opacity: 0.5;
  cursor: not-allowed;
}

::v-deep .custom-control-input:disabled ~ .custom-control-label {
  color: #6c757d;
  cursor: not-allowed;
}

::v-deep .delete-btn:hover:not(:disabled) {
  background-color: transparent;
  border-color: #dd3333;
  color: #dd3333;
}

::v-deep .delete-btn:disabled {
  border-color: #ebebeb !important;
  color: #ebebeb !important;
  cursor: not-allowed;
}

.label-description {
  font-size: 14px;
  font-weight: 400;
  color: #6d747d;
}
</style>
